import { Expose, Type } from 'class-transformer';
import { IsBoolean, IsEnum, IsNumber, IsOptional, IsString, ValidateNested } from 'class-validator';
import { Condominium, CondominiumType } from '../../../entities';
import Address from '../../../entities/address.entity';

type CreateCondominium = Omit<
  Condominium,
  | 'id'
  | 'created'
  | 'updated'
  | 'isChatEnabled'
  | 'isReservationsEnabled'
  | 'isSignupEnabled'
  | 'isEmergencyEnabled'
  | 'isBikesEnabled'
  | 'isFeedEnabled'
  | 'isDwellerPostEnabled'
  | 'isDwellerCommentEnabled'
  | 'isGuestsEnabled'
  | 'isHelpEnabled'
  | 'isPackagesEnabled'
> & {
  isChatEnabled?: boolean;
  isReservationsEnabled?: boolean;
  isBikesEnabled?: boolean;
  isFeedEnabled?: boolean;
  isGuestsEnabled?: boolean;
  isHelpEnabled?: boolean;
  isPackagesEnabled?: boolean;
};

export default class CreateCondominiumInput implements CreateCondominium {
  @IsString()
  readonly name: string;

  @IsString()
  readonly address: string;

  @IsString()
  @IsOptional()
  readonly cnpj?: string;

  @IsString()
  @IsOptional()
  @Expose({ name: 'bond_code' })
  readonly bondCode?: string;

  @IsString()
  @IsOptional()
  @Expose({ name: 'avatar_url' })
  readonly avatarUrl?: string | null;

  @ValidateNested()
  @Type(() => Address)
  @IsOptional()
  @Expose({ name: 'full_address' })
  readonly fullAddress?: Address;

  @IsOptional()
  @IsBoolean()
  @Expose({ name: 'is_chat_enabled' })
  readonly isChatEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  @Expose({ name: 'is_reservations_enabled' })
  readonly isReservationsEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  @Expose({ name: 'is_signup_enabled' })
  readonly isSignupEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  @Expose({ name: 'is_emergency_enabled' })
  readonly isEmergencyEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  @Expose({ name: 'is_bikes_enabled' })
  readonly isBikesEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  @Expose({ name: 'is_feed_enabled' })
  readonly isFeedEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  @Expose({ name: 'is_guests_enabled' })
  readonly isGuestsEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  @Expose({ name: 'is_help_enabled' })
  readonly isHelpEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  @Expose({ name: 'is_packages_enabled' })
  readonly isPackagesEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  @Expose({ name: 'is_dweller_post_enabled' })
  readonly isDwellerPostEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  @Expose({ name: 'is_dweller_comment_enabled' })
  readonly isDwellerCommentEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  @Expose({ name: 'laundry_id' })
  readonly laundryId?: string;

  @IsString()
  @IsOptional()
  @Expose({ name: 'furniture_id '})
  readonly furnitureId?: string;

  @IsString()
  @IsOptional()
  @Expose({ name: 'cleaning_id' })
  readonly cleaningId?: string;

  @IsString()
  @IsOptional()
  @Expose({ name: 'maid_id' })
  readonly maidId?: string;

  @IsOptional()
  @IsBoolean()
  readonly phone?: string;

  @IsOptional()
  @IsEnum(CondominiumType)
  readonly type?: CondominiumType;

  @IsOptional()
  @IsString()
  @Expose({ name: 'syndic_name' })
  readonly syndicName?: string;

  @IsString()
  @Expose({ name: 'syndic_phone' })
  @IsOptional()
  readonly syndicPhone?: string;

  @IsString()
  @Expose({ name: 'caretaker_name' })
  readonly caretakerName?: string;

  @IsString()
  @Expose({ name: 'caretaker_phone' })
  readonly caretakerPhone?: string;

  @IsOptional()
  @IsString()
  @Expose({ name: 'contact_email' })
  readonly contactEmail?: string;

  @IsNumber()
  @IsOptional()
  @Expose({ name: 'reservation_max_period' })
  readonly reservationMaxPeriod?: number;

  @IsBoolean()
  @Expose({ name: 'is_disabled' })
  readonly isDisabled?: boolean;

  constructor(init?: CreateCondominiumInput) {
    this.name = init?.name!;
    this.address = init?.address!;
    this.cnpj = init?.cnpj;
    this.bondCode = init?.bondCode;
    this.avatarUrl = init?.avatarUrl;
    this.fullAddress = init?.fullAddress;
    this.isChatEnabled = init?.isChatEnabled;
    this.isReservationsEnabled = init?.isReservationsEnabled;
    this.isSignupEnabled = init?.isSignupEnabled;
    this.isEmergencyEnabled = init?.isEmergencyEnabled;
    this.isBikesEnabled = init?.isBikesEnabled;
    this.isFeedEnabled = init?.isFeedEnabled;
    this.isGuestsEnabled = init?.isGuestsEnabled;
    this.isHelpEnabled = init?.isHelpEnabled;
    this.isPackagesEnabled = init?.isPackagesEnabled;
    this.laundryId = init?.laundryId;
    this.furnitureId = init?.furnitureId;
    this.cleaningId = init?.cleaningId;
    this.maidId = init?.maidId;
    this.phone = init?.phone;
    this.type = init?.type;
    this.syndicName = init?.syndicName;
    this.syndicPhone = init?.syndicPhone;
    this.caretakerName = init?.caretakerName;
    this.caretakerPhone = init?.caretakerPhone;
    this.contactEmail = init?.contactEmail;
    this.reservationMaxPeriod = init?.reservationMaxPeriod;
    this.isDwellerCommentEnabled = init?.isDwellerCommentEnabled;
    this.isDwellerPostEnabled = init?.isDwellerPostEnabled;
    this.isDisabled = init?.isDisabled;
  }
}
